import React from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { GlassCard } from '../ui/GlassCard';
import { RefreshButton } from '../ui/RefreshButton';
import { ValueStepper } from '../../modules/Onboarding/components/ValueStepper';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { formatCurrency } from '../charts/d3/format';
import type { LoanCostSummary, LoanPaidToDate } from '../../utils/loanAmortization';
import {
  formatLoanDuration,
  formatPayoffMonth,
  loanDurationParts,
} from '../../utils/loanAmortization';
import { LoanProgressBlock } from './LoanProgressBlock';
import { LoanStartMonthField } from './LoanStartMonthField';

type LoanSummaryCardProps = {
  label: string;
  principal: number;
  interestRatePct: number;
  termYears: number;
  monthlyPayment: number;
  monthsToPayoff: number;
  cost: LoanCostSummary;
  baselineCost: LoanCostSummary;
  monthsSaved: number;
  interestSaved: number;
  hasAnyExtra: boolean;
  startMonth: string | null;
  paidToDate: LoanPaidToDate | null;
  onPrincipalChange: (value: number) => void;
  onRateChange: (value: number) => void;
  onTermChange: (value: number) => void;
  onStartMonthChange: (value: string | null) => void;
};

export function LoanSummaryCard({
  label,
  principal,
  interestRatePct,
  termYears,
  monthlyPayment,
  monthsToPayoff,
  cost,
  baselineCost,
  monthsSaved,
  interestSaved,
  hasAnyExtra,
  startMonth,
  paidToDate,
  onPrincipalChange,
  onRateChange,
  onTermChange,
  onStartMonthChange,
}: LoanSummaryCardProps) {
  const payoffDate = formatPayoffMonth(startMonth, monthsToPayoff);
  const monthsLeft = paidToDate?.monthsRemaining ?? monthsToPayoff;
  const timeLeft = loanDurationParts(monthsLeft);
  return (
    <GlassCard style={styles.card}>
      <View style={styles.titleRow}>
        <Text style={styles.title}>{label}</Text>
        <RefreshButton compact />
      </View>
      <Text style={styles.hint}>Adjust amount, rate, term, or start month — payment updates instantly</Text>

      <View style={styles.paymentHero}>
        <Text style={styles.heroLabel}>Monthly payment</Text>
        <Text style={styles.heroValue}>{formatCurrency(monthlyPayment)}</Text>
      </View>

      <View
        style={styles.payoffRow}
        accessibilityRole="text"
        accessibilityLabel={
          monthsLeft > 0
            ? `Payoff ${payoffDate ?? formatLoanDuration(monthsToPayoff)}, ${timeLeft.years} years ${timeLeft.months} months left`
            : 'Paid off'
        }
      >
        <View style={styles.payoffItem}>
          <Text style={styles.heroLabel}>Payoff</Text>
          <Text style={styles.payoffValue} numberOfLines={1}>
            {payoffDate ?? formatLoanDuration(monthsToPayoff)}
          </Text>
        </View>
        <View style={styles.payoffItem}>
          <Text style={styles.heroLabel}>Years left</Text>
          <Text style={styles.payoffValue}>{monthsLeft > 0 ? timeLeft.years : 0}</Text>
        </View>
        <View style={styles.payoffItem}>
          <Text style={styles.heroLabel}>Months left</Text>
          <Text style={styles.payoffValue}>{monthsLeft > 0 ? timeLeft.months : 0}</Text>
        </View>
      </View>

      <ValueStepper
        label="Loan amount"
        value={principal}
        prefix="$"
        min={0}
        onChange={onPrincipalChange}
        onDecrease={() => onPrincipalChange(Math.max(0, principal - 5000))}
        onIncrease={() => onPrincipalChange(principal + 5000)}
      />
      <ValueStepper
        label="Interest rate"
        value={interestRatePct}
        suffix="%"
        decimals
        min={0}
        max={30}
        onChange={onRateChange}
        onDecrease={() =>
          onRateChange(Math.max(0, Math.round((interestRatePct - 0.25) * 100) / 100))
        }
        onIncrease={() =>
          onRateChange(Math.min(30, Math.round((interestRatePct + 0.25) * 100) / 100))
        }
      />
      <ValueStepper
        label="Term"
        value={termYears}
        suffix="yr"
        min={1}
        max={40}
        onChange={onTermChange}
        onDecrease={() => onTermChange(Math.max(1, termYears - 1))}
        onIncrease={() => onTermChange(Math.min(40, termYears + 1))}
      />
      <LoanStartMonthField value={startMonth} onChange={onStartMonthChange} />
      {paidToDate && startMonth ? (
        <LoanProgressBlock
          progress={paidToDate}
          startMonth={startMonth}
          monthsToPayoff={monthsToPayoff}
        />
      ) : null}

      <View style={styles.costBlock}>
        <Text style={styles.costTitle}>Total cost of loan</Text>
        <Text style={styles.costHero}>{formatCurrency(cost.totalCost)}</Text>
        <Text style={styles.costSub}>
          Principal {formatCurrency(cost.principal)} + interest{' '}
          {formatCurrency(cost.totalInterest)}
        </Text>

        <View style={styles.costTrack}>
          <View
            style={[
              styles.costPrincipal,
              { flex: Math.max(cost.principalSharePct, 1) },
            ]}
          />
          <View
            style={[
              styles.costInterest,
              { flex: Math.max(cost.interestSharePct, 1) },
            ]}
          />
        </View>
        <View style={styles.costLegend}>
          <Text style={styles.legendPrincipal}>
            Principal {cost.principalSharePct}%
          </Text>
          <Text style={styles.legendInterest}>
            Interest {cost.interestSharePct}%
          </Text>
        </View>

        <View style={styles.kpiRow}>
          <View style={styles.kpi}>
            <Text style={styles.kpiLabel}>You borrow</Text>
            <Text style={styles.kpiValue}>{formatCurrency(cost.principal)}</Text>
          </View>
          <View style={styles.kpi}>
            <Text style={styles.kpiLabel}>You pay in interest</Text>
            <Text style={[styles.kpiValue, styles.warning]}>
              {formatCurrency(cost.totalInterest)}
            </Text>
          </View>
        </View>

        {hasAnyExtra && interestSaved > 0 && (
          <View style={styles.savingsBanner}>
            <Text style={styles.savingsTitle}>Extras save you</Text>
            <Text style={styles.savingsValue}>{formatCurrency(interestSaved)}</Text>
            <Text style={styles.savingsHint}>
              in interest
              {monthsSaved > 0 ? ` · ${formatLoanDuration(monthsSaved)} faster` : ''}
            </Text>
            <Text style={styles.savingsCompare}>
              Was {formatCurrency(baselineCost.totalCost)} total → now{' '}
              {formatCurrency(cost.totalCost)}
            </Text>
          </View>
        )}
      </View>
    </GlassCard>
  );
}

const styles = StyleSheet.create({
  card: {
    gap: spacing.sm,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  title: {
    ...typography.headline,
    color: colors.text,
  },
  hint: {
    ...typography.caption,
    color: colors.textMuted,
    marginBottom: spacing.xs,
  },
  paymentHero: {
    gap: 4,
    marginBottom: spacing.xs,
  },
  heroLabel: {
    ...typography.label,
    color: colors.textMuted,
    textTransform: 'uppercase',
  },
  heroValue: {
    ...typography.display,
    fontSize: 32,
    color: colors.accent,
    fontVariant: ['tabular-nums'],
  },
  payoffRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: spacing.sm,
    paddingTop: spacing.md,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border,
  },
  payoffItem: {
    flex: 1,
    gap: 4,
    minWidth: 0,
  },
  payoffValue: {
    ...typography.headline,
    fontSize: 20,
    color: colors.text,
    fontVariant: ['tabular-nums'],
  },
  costBlock: {
    marginTop: spacing.sm,
    paddingTop: spacing.md,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border,
    gap: spacing.sm,
  },
  costTitle: {
    ...typography.label,
    color: colors.textMuted,
    textTransform: 'uppercase',
  },
  costHero: {
    ...typography.display,
    fontSize: 28,
    color: colors.text,
    fontVariant: ['tabular-nums'],
  },
  costSub: {
    ...typography.caption,
    color: colors.textMuted,
    fontVariant: ['tabular-nums'],
    marginTop: -4,
  },
  costTrack: {
    flexDirection: 'row',
    height: 12,
    borderRadius: radii.pill,
    overflow: 'hidden',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    marginTop: spacing.xs,
  },
  costPrincipal: {
    backgroundColor: colors.accent,
  },
  costInterest: {
    backgroundColor: colors.warning,
  },
  costLegend: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  legendPrincipal: {
    ...typography.caption,
    color: colors.accent,
  },
  legendInterest: {
    ...typography.caption,
    color: colors.warning,
  },
  kpiRow: {
    flexDirection: 'row',
    gap: spacing.md,
    marginTop: spacing.xs,
  },
  kpi: {
    flex: 1,
    gap: 2,
  },
  kpiLabel: {
    ...typography.label,
    color: colors.textMuted,
    textTransform: 'uppercase',
  },
  kpiValue: {
    ...typography.headline,
    fontSize: 18,
    color: colors.text,
    fontVariant: ['tabular-nums'],
  },
  warning: {
    color: colors.warning,
  },
  savingsBanner: {
    marginTop: spacing.sm,
    padding: spacing.md,
    borderRadius: radii.md,
    backgroundColor: 'rgba(61, 214, 164, 0.1)',
    borderWidth: 1,
    borderColor: 'rgba(61, 214, 164, 0.25)',
    gap: 2,
  },
  savingsTitle: {
    ...typography.label,
    color: colors.accent,
    textTransform: 'uppercase',
  },
  savingsValue: {
    ...typography.headline,
    fontSize: 24,
    color: colors.accent,
    fontVariant: ['tabular-nums'],
  },
  savingsHint: {
    ...typography.caption,
    color: colors.text,
  },
  savingsCompare: {
    ...typography.caption,
    color: colors.textMuted,
    marginTop: spacing.xs,
    fontVariant: ['tabular-nums'],
  },
});
